import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Input } from "@bcc/components/ui/input";
import { downloadCsv, flattenObject, type FlattenedObject } from "@client-shared";
import { type Table } from "@tanstack/react-table";
import { Download, Search, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { DataTableColumnVisibility } from "./data-table-column-visibility";
import type { DataTableFilter, DataTableFilterSelection, DataTableRow, PromotedAction } from "./data-table.types";
import {
  setClearFiltersButtonTestId,
  setExportButtonTestId,
  setFilterBadgeTestId,
  setFilterBarTestId,
  setFilterButtonTestId,
  setPromotedActionTestId,
  setSearchClearButtonTestId,
  setSearchInputTestId,
} from "./data-table.utils";

// Stable empty arrays to prevent unnecessary re-renders
const EMPTY_FILTERS: DataTableFilter[] = [];
const EMPTY_PROMOTED_ACTIONS: PromotedAction[] = [];

/**
 * Maps restored `{ column, value }` selections back onto the current filter list.
 *
 * Selections are persisted as stable column/value pairs rather than localized labels,
 * so a language switch (or a bag size disappearing from vendor config) can't resurrect
 * a chip that no longer exists — unmatched entries are simply dropped. Returns
 * `undefined` when there is nothing persisted, so callers can fall back to
 * `defaultSelected`.
 *
 * Collapsed to one selection per column (last wins) to match the radio semantics of
 * `handleFilterToggle`. Without this, a hand-edited storage payload holding two values
 * for one column would light up both chips while only the last column filter applied.
 */
const matchRestoredFilters = (filters: DataTableFilter[], restored?: DataTableFilterSelection[]): DataTableFilter[] | undefined => {
  if (restored === undefined) return undefined;
  const byColumn = restored.reduce<Map<string, DataTableFilter>>((matched, selection) => {
    const filter = filters.find((f) => f.column === selection.column && f.value === selection.value);
    if (filter) matched.set(selection.column, filter);
    return matched;
  }, new Map());
  return Array.from(byColumn.values());
};

// Separator for composite chip keys. A control character can't appear in a column id or a
// config-sourced filter value, so it can't be confused with the data around it.
const FILTER_KEY_SEPARATOR = "\u0000";

/**
 * Stable identity for a chip: `column`/`value` when both are present, else the label.
 *
 * Keying selections on the localized label alone means an in-session language switch — which
 * `VendorProvider` can trigger via `i18n.changeLanguage` from a network-sourced locale after
 * mount — rewrites every `filter.label` and silently unhighlights each active chip while its
 * column filter stays applied. Column and value are locale-independent, so the highlight
 * survives. Label-only chips (custom `filterFn`, no column/value) keep label identity; nothing
 * more stable exists for them.
 */
const getFilterKey = (filter: DataTableFilter): string =>
  filter.column !== undefined && filter.value !== undefined ? `${filter.column}${FILTER_KEY_SEPARATOR}${filter.value}` : filter.label;

/** Decodes a chip key back into its column/value pair; `undefined` for a label-only chip. */
const parseFilterKey = (key: string): DataTableFilterSelection | undefined => {
  const separatorIndex = key.indexOf(FILTER_KEY_SEPARATOR);
  return separatorIndex === -1
    ? undefined
    : { column: key.slice(0, separatorIndex), value: key.slice(separatorIndex + FILTER_KEY_SEPARATOR.length) };
};

/**
 * Columns that currently offer at least one option. Used to tell "this value was genuinely
 * removed" (column has options, none match) from "these options haven't loaded yet" (column has
 * no options at all) — the distinction both the restore and the orphan paths turn on.
 */
const getColumnsWithOptions = (filters: DataTableFilter[]): Set<string> =>
  new Set(
    filters.reduce<string[]>((columns, filter) => {
      if (filter.column !== undefined) columns.push(filter.column);
      return columns;
    }, [])
  );

/**
 * Reduces the active chips to persistable pairs. Label-only chips (custom `filterFn` with no
 * column/value) have no stable identity and are not persisted.
 */
const toFilterSelections = (filters: DataTableFilter[], activeKeys: Set<string>): DataTableFilterSelection[] =>
  filters.reduce<DataTableFilterSelection[]>((selections, filter) => {
    if (activeKeys.has(getFilterKey(filter)) && filter.column !== undefined && filter.value !== undefined) {
      selections.push({ column: filter.column, value: filter.value });
    }
    return selections;
  }, []);

/**
 * The payload to persist: the active chips plus anything still waiting for its options, so a
 * selection that hasn't resolved yet isn't erased by an unrelated write. Active chips win per
 * column — they are the live, user-visible state.
 */
const buildPersistedSelections = (
  filters: DataTableFilter[],
  activeKeys: Set<string>,
  pending: DataTableFilterSelection[]
): DataTableFilterSelection[] => {
  const active = toFilterSelections(filters, activeKeys);
  const activeColumns = new Set(active.map((selection) => selection.column));
  return [...active, ...pending.filter((selection) => !activeColumns.has(selection.column))];
};

interface DataTableFilterBarProps<T extends object> {
  table: Table<DataTableRow<T>>;
  searchable?: boolean;
  searchPlaceholder?: string;
  filters?: DataTableFilter[];
  exportable?: boolean;
  exportFilename?: string;
  exportColumns?: string[];
  promotedActions?: PromotedAction[];
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  searchDebounceMs?: number;
  // i18n props
  exportText?: string;
  clearText?: string;
  searchBadgePrefix?: string;
  exportToCSV?: boolean;
  onExportRows?: (rows: unknown[]) => void;
  /**
   * Restored chip selections. Seeds the chip state and rehydrates the matching column
   * filters; selections whose filter option has not loaded yet are retried as `filters`
   * changes, and abandoned once the user makes their own choice. Not a controlled value.
   */
  initialActiveFilters?: DataTableFilterSelection[];
  /** Fired whenever chip selections change, with locale-independent `{ column, value }` pairs. */
  onActiveFiltersChange?: (selections: DataTableFilterSelection[]) => void;
  /** Render the "Columns" show/hide menu next to the Export button. */
  showColumnVisibility?: boolean;
  /** Restore default column visibility and drop the persisted override. */
  onResetColumnVisibility?: () => void;
  /** Resolves a display label for a column id in the "Columns" menu. */
  getColumnLabel?: (columnId: string) => string;
}

/**
 * DataTableFilterBar
 *
 * Renders the filter bar with search input, filter tabs,
 * export button, and promoted actions.
 */
export function DataTableFilterBar<T extends object>({
  table,
  searchable = false,
  searchPlaceholder: searchPlaceholderProp,
  filters = EMPTY_FILTERS,
  exportable = false,
  exportFilename = "export",
  exportColumns,
  promotedActions = EMPTY_PROMOTED_ACTIONS,
  globalFilter,
  setGlobalFilter,
  searchDebounceMs = 300,
  exportText: exportTextProp,
  clearText: clearTextProp,
  searchBadgePrefix: searchBadgePrefixProp,
  exportToCSV,
  onExportRows,
  initialActiveFilters,
  onActiveFiltersChange,
  showColumnVisibility = false,
  onResetColumnVisibility,
  getColumnLabel,
}: DataTableFilterBarProps<T>) {
  const { t } = useTranslation();
  // Use translations with prop overrides
  const searchPlaceholder = searchPlaceholderProp ?? t("dataTable.searchPlaceholder");
  const exportText = exportTextProp ?? t("dataTable.export");
  const clearText = clearTextProp ?? t("dataTable.clear");
  const searchBadgePrefix = searchBadgePrefixProp ?? t("dataTable.searchBadge");

  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Chip selections restored from persistence, resolved against the current filter list.
  // A persisted (possibly empty) entry wins over `defaultSelected`. Seeded synchronously so
  // the restored chips are highlighted on first paint rather than one frame late.
  const [activeFilters, setActiveFilters] = React.useState<Set<string>>(
    () => new Set((matchRestoredFilters(filters, initialActiveFilters) ?? filters.filter((f) => f.defaultSelected)).map(getFilterKey))
  );

  // Restored selections still waiting for a matching filter option. Options can arrive
  // asynchronously — Products' bag sizes come from vendor config, which is `undefined` on the
  // first renders — so a one-shot match on mount would silently drop them.
  const pendingRestoredRef = React.useRef(initialActiveFilters);

  // Rehydrate the TanStack column filters from the same matched list that seeds the chip Set,
  // so highlight and filtering can never disagree. Replaying the selections through
  // `handleFilterToggle` instead would collapse the Set: every call derives its next value
  // from the same stale closure, so only the last label would survive.
  React.useEffect(() => {
    const pending = pendingRestoredRef.current;
    if (pending === undefined || pending.length === 0) return;

    const matched = matchRestoredFilters(filters, pending) ?? [];
    // Once a column resolves, any other pending selection for it is moot (radio semantics).
    const resolvedColumns = new Set(matched.map((filter) => filter.column));
    const columnsWithOptions = getColumnsWithOptions(filters);

    // A pending selection whose column now offers options but none matching its value has been
    // genuinely removed, not merely not-yet-loaded. Left pending it would outlive every reload
    // and could spring back to life if that value were ever re-added during this mount.
    const stale = pending.filter((selection) => !resolvedColumns.has(selection.column) && columnsWithOptions.has(selection.column));
    if (matched.length === 0 && stale.length === 0) return;

    pendingRestoredRef.current = pending.filter(
      (selection) => !resolvedColumns.has(selection.column) && !columnsWithOptions.has(selection.column)
    );

    matched.forEach((filter) => {
      if (filter.column && filter.value) {
        table.getColumn(filter.column)?.setFilterValue(filter.value);
      }
    });

    const nextActive = new Set([...activeFilters, ...matched.map(getFilterKey)]);

    setActiveFilters(nextActive);

    // Rewrite storage only to forget the dead selections; resolving a chip changes nothing that
    // wasn't already persisted.
    if (stale.length > 0) {
      onActiveFiltersChange?.(buildPersistedSelections(filters, nextActive, pendingRestoredRef.current));
    }
  }, [filters, table, activeFilters, onActiveFiltersChange]);

  // A filter option can disappear after its chip was selected — a bag size dropped from vendor
  // config, say. The key would linger in `activeFilters` matching nothing, so no badge and no
  // Clear control would render while its column filter stayed applied: an inexplicably filtered
  // (possibly empty) table with no way out. Drop orphaned keys and clear the column filter they
  // left behind, keeping the chips and the filtering in agreement.
  React.useEffect(() => {
    if (activeFilters.size === 0) return;

    const liveKeys = new Set(filters.map(getFilterKey));
    const orphaned = Array.from(activeFilters).filter((key) => !liveKeys.has(key));
    if (orphaned.length === 0) return;

    // A column that still offers options has genuinely dropped this value; a column with no
    // options at all is more likely mid-reload than genuinely emptied.
    const columnsWithOptions = getColumnsWithOptions(filters);

    const requeued: DataTableFilterSelection[] = [];
    let droppedForGood = false;

    orphaned.forEach((key) => {
      const selection = parseFilterKey(key);
      // Label-only chips carry no column, so there is no column filter to clear.
      if (selection === undefined) return;
      table.getColumn(selection.column)?.setFilterValue(undefined);
      if (columnsWithOptions.has(selection.column)) {
        droppedForGood = true;
      } else {
        requeued.push(selection);
      }
    });

    // Hand mid-reload selections back to the pending-restore effect so returning options reapply
    // them in this session. Keeping the stored entry alone would only help after a page reload,
    // which is the wrong remedy for something that resolves within the same mount.
    if (requeued.length > 0) {
      pendingRestoredRef.current = [...(pendingRestoredRef.current ?? []), ...requeued];
    }

    const surviving = new Set(Array.from(activeFilters).filter((key) => liveKeys.has(key)));
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reconcile chip state with the filter list once options change
    setActiveFilters(surviving);

    // Only forget the stored selection for an option we're confident is gone for good. Anything
    // still pending — including what was just requeued — has to be carried through, or a single
    // `filters` update that both removes one column's value and empties another column's list
    // would persist away the requeued selection and lose it across a reload. Surviving chips win
    // per column, since they are the live, user-visible state.
    if (droppedForGood) {
      onActiveFiltersChange?.(buildPersistedSelections(filters, surviving, pendingRestoredRef.current ?? []));
    }
  }, [filters, table, activeFilters, onActiveFiltersChange]);

  // The user's own choices supersede anything still pending, so a late-arriving filter option
  // can't resurrect a chip they just dismissed.
  const discardPendingRestored = () => {
    pendingRestoredRef.current = undefined;
  };

  // Local state for immediate input updates, debounced to setGlobalFilter
  const [localSearchValue, setLocalSearchValue] = React.useState(globalFilter);
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local value when globalFilter changes externally
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync local search input value when globalFilter changes externally
    setLocalSearchValue(globalFilter);
  }, [globalFilter]);

  // Handle CSV export
  const handleExport = React.useCallback(() => {
    if (onExportRows) {
      const filteredRows = table.getFilteredRowModel().rows.map((r) => r.original);
      onExportRows(filteredRows);
      return;
    }

    const rows = table.getFilteredRowModel().rows;
    const data: FlattenedObject[] = rows.map((row) => {
      const original = row.original as Record<string, unknown>;
      return flattenObject(original);
    });

    // Filter columns if exportColumns is specified
    let filteredData = data;
    if (exportColumns && exportColumns.length > 0) {
      filteredData = data.map((row) => {
        const filtered: FlattenedObject = {};
        for (const key of Object.keys(row)) {
          if (exportColumns.some((col) => key === col || key.startsWith(`${col}.`))) {
            filtered[key] = row[key];
          }
        }
        return filtered;
      });
    }

    try {
      // Simple CSV conversion
      if (filteredData.length === 0) return;

      const headers = Object.keys(filteredData[0] ?? {});
      // Map headers to custom export headers if defined
      const displayHeaders = headers.map((header) => {
        const column = table.getAllColumns().find((col) => {
          if (col.id === header) return true;
          // Type guard for accessorKey
          const columnDef = col.columnDef as { accessorKey?: string };
          return columnDef.accessorKey === header;
        });

        // Type guard for meta with exportHeader
        const meta = column?.columnDef.meta as { exportHeader?: string } | undefined;
        return meta?.exportHeader ?? header;
      });

      const csvRows = [
        displayHeaders.join(","),
        ...filteredData.map((row) =>
          headers
            .map((header) => {
              const val = row[header];
              if (val == null) return "";
              const str = String(val);
              // Escape quotes and wrap in quotes if contains comma, quote, or newline
              if (str.includes(",") || str.includes('"') || str.includes("\n")) {
                return `"${str.replace(/"/g, '""')}"`;
              }
              return str;
            })
            .join(",")
        ),
      ];
      const csv = csvRows.join("\n");
      downloadCsv(csv, exportFilename);
    } catch (error) {
      console.error("Failed to export CSV:", error);
    }
  }, [onExportRows, table, exportColumns, exportFilename]);

  React.useEffect(() => {
    if (exportToCSV) {
      handleExport();
    }
  }, [exportToCSV, handleExport]);

  // Debounced search handler
  const handleSearchChange = React.useCallback(
    (value: string) => {
      setLocalSearchValue(value);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setGlobalFilter(value);
      }, searchDebounceMs);
    },
    [setGlobalFilter, searchDebounceMs]
  );

  // Clear search immediately (no debounce)
  const handleClearSearch = React.useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setLocalSearchValue("");
    setGlobalFilter("");
  }, [setGlobalFilter]);

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Keyboard shortcut for search (Cmd/Ctrl + F)
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f" && searchable) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === "Escape") {
        searchInputRef.current?.blur();
        handleClearSearch();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [searchable, handleClearSearch]);

  // Handle filter toggle
  const handleFilterToggle = (filter: DataTableFilter) => {
    discardPendingRestored();
    const filterKey = getFilterKey(filter);
    const newActiveFilters = new Set(activeFilters);
    if (activeFilters.has(filterKey)) {
      newActiveFilters.delete(filterKey);
      // Clear the column filter if it exists
      if (filter.column) {
        table.getColumn(filter.column)?.setFilterValue(undefined);
      }
    } else {
      // Deactivate any other active filter targeting the same column (radio semantics)
      if (filter.column) {
        filters
          .filter((f) => f.column === filter.column && newActiveFilters.has(getFilterKey(f)))
          .forEach((f) => newActiveFilters.delete(getFilterKey(f)));
      }
      newActiveFilters.add(filterKey);
      // Apply the column filter if it exists
      if (filter.column && filter.value) {
        table.getColumn(filter.column)?.setFilterValue(filter.value);
      }
      // Call custom filter function if provided
      if (filter.filterFn) {
        filter.filterFn();
      }
    }
    setActiveFilters(newActiveFilters);
    onActiveFiltersChange?.(toFilterSelections(filters, newActiveFilters));
  };

  // Clear all filters
  const handleClearFilters = () => {
    discardPendingRestored();
    handleClearSearch();
    setActiveFilters(new Set());
    table.resetColumnFilters();
    onActiveFiltersChange?.([]);
  };

  // Resolved against the *current* filter list, so the badges re-render with the new copy after a
  // language switch instead of showing labels captured when the chip was first selected.
  const activeFilterList = filters.filter((filter) => activeFilters.has(getFilterKey(filter)));
  const hasActiveFilters = (searchable && localSearchValue) || activeFilterList.length > 0;

  return (
    <div data-testid={setFilterBarTestId()} className="flex flex-col gap-4 px-4 py-3 border-b border-border bg-card">
      <div className="flex items-center justify-between gap-4">
        {/* Left side - Search and filters */}
        <div className="flex items-center gap-4 flex-1">
          {searchable && (
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                data-testid={setSearchInputTestId()}
                placeholder={searchPlaceholder}
                value={localSearchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 pr-9"
              />
              {localSearchValue && (
                <button
                  type="button"
                  data-testid={setSearchClearButtonTestId()}
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>
          )}

          {/* Filter tabs */}
          {filters.length > 0 && (
            <div className="flex items-center gap-2">
              {filters.map((filter, index) => (
                <Button
                  key={filter.label}
                  data-testid={setFilterButtonTestId(index)}
                  variant={activeFilters.has(getFilterKey(filter)) ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleFilterToggle(filter)}
                  className="h-8"
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-2">
          {/* Clear filters */}
          {hasActiveFilters && (
            <Button data-testid={setClearFiltersButtonTestId()} variant="ghost" size="sm" onClick={handleClearFilters} className="h-8">
              <X className="size-4 mr-1" />
              {clearText}
            </Button>
          )}

          {/* Promoted actions */}
          {promotedActions.map((action) => (
            <Button
              key={action.label}
              data-testid={setPromotedActionTestId(action.label)}
              variant="outline"
              size="sm"
              onClick={action.onClick}
              className="h-8"
            >
              {action.label}
            </Button>
          ))}

          {/* Column show/hide menu */}
          {showColumnVisibility && onResetColumnVisibility && (
            <DataTableColumnVisibility table={table} onReset={onResetColumnVisibility} getColumnLabel={getColumnLabel} />
          )}

          {/* Export button */}
          {exportable && (
            <Button data-testid={setExportButtonTestId()} variant="outline" size="sm" onClick={handleExport} className="h-8">
              <Download className="size-4 mr-1" />
              {exportText}
            </Button>
          )}
        </div>
      </div>

      {/* Active filter badges */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2">
          {searchable && localSearchValue && (
            <Badge variant="secondary" className="gap-1">
              {searchBadgePrefix} {localSearchValue}
              <button type="button" onClick={handleClearSearch} className="ml-1 hover:text-foreground">
                <X className="size-3" />
              </button>
            </Badge>
          )}
          {activeFilterList.map((filter) => (
            <Badge key={getFilterKey(filter)} data-testid={setFilterBadgeTestId(filter.label)} variant="secondary" className="gap-1">
              {filter.label}
              <button type="button" onClick={() => handleFilterToggle(filter)} className="ml-1 hover:text-foreground">
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
